/**
 * Primitive value schemas shared by every surface.
 *
 * Australia-only by decision (spec §18, D-009): AU states, ABN as the business
 * identifier, DD/MM/YYYY for display. The API itself always speaks ISO-8601 — display
 * formatting is a presentation concern and never reaches the wire.
 */
import { z } from 'zod';
export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
export const auStateSchema = z.enum(AU_STATES);
export const AU_STATE_LABELS = {
    ACT: 'Australian Capital Territory',
    NSW: 'New South Wales',
    NT: 'Northern Territory',
    QLD: 'Queensland',
    SA: 'South Australia',
    TAS: 'Tasmania',
    VIC: 'Victoria',
    WA: 'Western Australia',
};
/**
 * ABN checksum, per the Australian Business Register algorithm:
 * subtract 1 from the first digit, apply the positional weights, and the weighted sum
 * must be divisible by 89. Catches transposition typos, which are the common case.
 * A valid checksum does not mean the ABN is registered — that needs the ABR Lookup
 * call, which is behind FEATURE_ABN_LOOKUP.
 */
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
export function isValidAbn(value) {
    const digits = value.replace(/\s/g, '');
    if (!/^\d{11}$/.test(digits))
        return false;
    let sum = 0;
    for (let i = 0; i < 11; i += 1) {
        const digit = Number(digits[i]) - (i === 0 ? 1 : 0);
        sum += digit * ABN_WEIGHTS[i];
    }
    return sum % 89 === 0;
}
export function formatAbn(value) {
    const d = value.replace(/\s/g, '');
    return d.length === 11 ? `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}` : value;
}
export const abnSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s/g, ''))
    .refine((v) => /^\d{11}$/.test(v), { message: 'An ABN is 11 digits' })
    .refine(isValidAbn, { message: 'That ABN fails its checksum — check for a transposed digit' });
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(320)
    .email({ message: 'Enter a valid email address' });
/** Permissive on purpose: AU landline, mobile, and international forms all appear. */
export const phoneSchema = z
    .string()
    .trim()
    .min(6)
    .max(32)
    .regex(/^[+()\d\s-]+$/, { message: 'Enter a valid phone number' });
export const postcodeSchema = z
    .string()
    .trim()
    .regex(/^\d{4}$/, { message: 'An Australian postcode is 4 digits' });
/** ISO 3166-1 alpha-2. Uppercased for us, because integrators send both cases. */
export const countryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(2, { message: 'Use a 2-letter ISO country code, e.g. AU' })
    .regex(/^[A-Z]{2}$/);
/**
 * URL-safe identifier used in routes and API payloads (`chamberId: "bcq"`).
 * Lowercase only — a slug that differs by case is two slugs to a human and one to nobody.
 */
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, {
    message: 'Use lowercase letters, numbers and hyphens',
});
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Use YYYY-MM-DD' })
    .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Not a real date' });
export const isoDateTimeSchema = z.string().datetime({ offset: true });
/**
 * Monetary and quantity values cross the wire as strings and are stored as Decimal.
 * A float here is a rounding error on an invoice total, which is exactly the mismatch
 * the completeness check (spec §8) exists to catch — so the system must not create it.
 */
export const decimalStringSchema = z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === 'number' ? v.toString() : v.trim()))
    .refine((v) => /^-?\d+(\.\d+)?$/.test(v), { message: 'Enter a number' });
export const positiveDecimalStringSchema = decimalStringSchema.refine((v) => Number(v) > 0, { message: 'Must be greater than zero' });
/**
 * HS tariff code. Six digits is the internationally harmonised level and is what the FTA
 * certificate forms ask for; 8 and 10 digit national extensions are accepted because
 * exporters routinely quote them. Dots and spaces are stripped.
 */
export const hsCodeSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s.]/g, ''))
    .refine((v) => /^\d{6}(\d{2})?(\d{2})?$/.test(v), {
    message: 'An HS code is 6, 8 or 10 digits',
});
export const auAddressSchema = z.object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).optional().nullable(),
    suburb: z.string().trim().min(1).max(100),
    state: auStateSchema,
    postcode: postcodeSchema,
    country: countryCodeSchema.default('AU'),
});
/** Overseas consignees have no AU state or 4-digit postcode. Kept deliberately loose. */
export const internationalAddressSchema = z.object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).optional().nullable(),
    suburb: z.string().trim().max(100).optional().nullable(),
    state: z.string().trim().max(100).optional().nullable(),
    postcode: z.string().trim().max(20).optional().nullable(),
    country: countryCodeSchema,
});
/**
 * ══════════════════════════════════════════════════════════════════════════
 * CALENDAR DATES vs INSTANTS
 *
 * A certificate date is a CALENDAR DATE. "23 September 2026" is what prints on the
 * document and what a customs authority reads; it is not a moment in time and it does not
 * move when you cross a timezone.
 *
 * `receivedAt` and `processedAt` are the opposite — genuine instants, correctly shown in
 * the reader's own local time.
 *
 * Conflating the two is how a chamber in Brisbane approving at 00:30 on the 23rd got a
 * certificate dated the 22nd: the dialog defaulted to `toISOString().slice(0, 10)`, which
 * is the date in UTC, and UTC was still ten hours behind. The number was drawn from the
 * wrong sequence period too, because `periodKeyFor` reads the same value.
 *
 * The rule, from here on:
 *   • a calendar date is chosen from the OPERATOR's local day        → todayCalendarDate()
 *   • it is stored as UTC midnight so the string round-trips exactly → toCalendarDate()
 *   • it is read back with UTC getters, everywhere, always           → formatAuCalendarDate()
 *
 * `periodKeyFor` already used UTC getters, so this makes the printed date and the
 * certificate number agree about which day it was.
 * ══════════════════════════════════════════════════════════════════════════
 */
/** The operator's own day as `YYYY-MM-DD`. Local getters, deliberately. */
export function todayCalendarDate(now = new Date()) {
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}
/**
 * `YYYY-MM-DD` → the Date stored in the database, pinned to UTC midnight.
 *
 * Explicit rather than `new Date(value)`, which is only defined for a bare date string.
 * Hand it a full ISO datetime and it silently keeps the time, and the certificate date
 * stops being a calendar date.
 */
export function toCalendarDate(value) {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? null
            : new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!match)
        return null;
    const [, y, m, d] = match;
    const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    return Number.isNaN(date.getTime()) ? null : date;
}
/**
 * A calendar date as DD/MM/YYYY. UTC getters, so what prints on the certificate and what
 * every screen shows is the day that was chosen — from any deployment timezone.
 */
export function formatAuCalendarDate(value) {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime()))
        return '';
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getUTCFullYear()}`;
}
/**
 * Display helper for an INSTANT — something that happened at a moment, shown in the
 * reader's local time. A certificate date is not one of these: use
 * `formatAuCalendarDate`.
 */
export function formatAuDate(value) {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime()))
        return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
}
//# sourceMappingURL=primitives.js.map